import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Calendar, MapPin, X, Upload } from "lucide-react";
import { format } from "date-fns";
import { MediaUploader, MediaDisplay } from "@/components/MediaComponents";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const categories = ["general", "awareness", "fundraiser", "community", "screening"];

const defaultTicketType = {
  name: "General Admission",
  description: "",
  price: 0,
  quantity: 100,
  early_bird: false,
  vip: false
};

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    end_date: "",
    location: "",
    address: "",
    image_url: "",
    media: [],
    category: "general",
    featured: false,
    organizer: "",
    ticket_types: [{ ...defaultTicketType }]
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?upcoming=false`, { withCredentials: true });
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setSelectedEvent(null);
    setFormData({
      title: "",
      description: "",
      date: "",
      end_date: "",
      location: "",
      address: "",
      image_url: "",
      category: "general",
      featured: false,
      organizer: "",
      ticket_types: [{ ...defaultTicketType }]
    });
    setDialogOpen(true);
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      date: new Date(event.date).toISOString().slice(0, 16),
      end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : "",
      location: event.location,
      address: event.address || "",
      image_url: event.image_url || "",
      media: event.media || [],
      category: event.category,
      featured: event.featured,
      organizer: event.organizer || "",
      ticket_types: event.ticket_types?.length > 0 ? event.ticket_types : [{ ...defaultTicketType }]
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        ...formData,
        date: new Date(formData.date).toISOString(),
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null
      };

      if (selectedEvent) {
        await axios.put(`${API}/events/${selectedEvent.event_id}`, payload, { withCredentials: true });
        toast.success("Event updated successfully");
      } else {
        await axios.post(`${API}/events`, payload, { withCredentials: true });
        toast.success("Event created successfully");
      }

      setDialogOpen(false);
      fetchEvents();
    } catch (error) {
      console.error("Error saving event:", error);
      toast.error(error.response?.data?.detail || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`${API}/events/${selectedEvent.event_id}`, { withCredentials: true });
      toast.success("Event deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Failed to delete event");
    }
  };

  const addTicketType = () => {
    setFormData(prev => ({
      ...prev,
      ticket_types: [...prev.ticket_types, { ...defaultTicketType, name: `Ticket Type ${prev.ticket_types.length + 1}` }]
    }));
  };

  const removeTicketType = (index) => {
    setFormData(prev => ({
      ...prev,
      ticket_types: prev.ticket_types.filter((_, i) => i !== index)
    }));
  };

  const updateTicketType = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      ticket_types: prev.ticket_types.map((tt, i) => 
        i === index ? { ...tt, [field]: value } : tt
      )
    }));
  };

  return (
    <AdminLayout currentPage="Events">
      <div className="p-8" data-testid="admin-events">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Events</h1>
            <p className="text-slate-600">Manage your events and ticket types</p>
          </div>
          <Button className="btn-primary" onClick={openCreateDialog} data-testid="create-event-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Event
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg p-6 h-24 animate-pulse"></div>
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 font-semibold text-slate-700">Event</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Date</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Location</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Category</th>
                  <th className="text-left p-4 font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {events.map(event => (
                  <tr key={event.event_id} data-testid={`event-row-${event.event_id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                      {(() => {
                        const mediaUrl = event.media?.[0]?.url;
                        const imageUrl = event.image_url;
                        const cleanImageUrl = imageUrl ? imageUrl.trim().replace(/^"|"$/g, '') : '';
                        const validImageUrl = cleanImageUrl && /^(https?:|\/)/.test(cleanImageUrl) ? cleanImageUrl : null;
                        const finalUrl = mediaUrl || validImageUrl;
                        return finalUrl ? (
                          <img
                            src={finalUrl}
                            alt={event.title}
                            className="w-12 h-12 rounded object-cover bg-slate-100"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-[10px]">No img</div>
                        );
                      })()}
                        <div>
                          <p className="font-medium text-slate-800">{event.title}</p>
                          {event.featured && (
                            <span className="text-xs bg-awareness-blue/10 text-awareness-blue px-2 py-0.5 rounded">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">
                      {format(new Date(event.date), "MMM d, yyyy")}
                    </td>
                    <td className="p-4 text-slate-600">{event.location}</td>
                    <td className="p-4">
                      <span className="capitalize bg-slate-100 px-2 py-1 rounded text-sm">
                        {event.category}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => openEditDialog(event)}
                          data-testid={`edit-event-${event.event_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => { setSelectedEvent(event); setDeleteDialogOpen(true); }}
                          data-testid={`delete-event-${event.event_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center">
            <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">No events yet</h3>
            <p className="text-slate-500 mb-4">Create your first event to get started</p>
            <Button className="btn-primary" onClick={openCreateDialog}>
              <Plus className="w-4 h-4 mr-2" />
              Create Event
            </Button>
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedEvent ? "Edit Event" : "Create Event"}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input 
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    required
                    data-testid="event-title"
                  />
                </div>

                <div>
                  <Label htmlFor="date">Start Date & Time *</Label>
                  <Input 
                    id="date"
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    required
                    data-testid="event-date"
                  />
                </div>

                <div>
                  <Label htmlFor="end_date">End Date & Time</Label>
                  <Input 
                    id="end_date"
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input 
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                    data-testid="event-location"
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat.charAt(0).toUpperCase() + cat.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="address">Full Address</Label>
                  <Input 
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea 
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={4}
                    required
                    data-testid="event-description"
                  />
                </div>

                <MediaUploader value={formData.media || []} onChange={(m) => setFormData(prev => ({...prev, media: m}))} label="Event Media (photos/video slideshow)" />

                <div>
                  <Label htmlFor="organizer">Organizer</Label>
                  <Input 
                    id="organizer"
                    value={formData.organizer}
                    onChange={(e) => setFormData({...formData, organizer: e.target.value})}
                  />
                </div>

                <div className="col-span-2 flex items-center space-x-2">
                  <Checkbox 
                    id="featured"
                    checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({...formData, featured: checked})}
                  />
                  <Label htmlFor="featured" className="cursor-pointer">Featured Event</Label>
                </div>
              </div>

              {/* Ticket Types */}
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <Label className="text-base">Ticket Types</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addTicketType}>
                    <Plus className="w-4 h-4 mr-1" /> Add Ticket Type
                  </Button>
                </div>
                
                <div className="space-y-4">
                  {formData.ticket_types.map((tt, index) => (
                    <div key={index} className="bg-slate-50 p-4 rounded-lg relative">
                      {formData.ticket_types.length > 1 && (
                        <Button 
                          type="button"
                          variant="ghost" 
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => removeTicketType(index)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Name</Label>
                          <Input 
                            value={tt.name}
                            onChange={(e) => updateTicketType(index, 'name', e.target.value)}
                          />
                        </div>
                        <div>
                          <Label>Price ($)</Label>
                          <Input 
                            type="number"
                            min="0"
                            step="0.01"
                            value={tt.price}
                            onChange={(e) => updateTicketType(index, 'price', parseFloat(e.target.value) || 0)}
                          />
                        </div>
                        <div>
                          <Label>Quantity</Label>
                          <Input 
                            type="number"
                            min="1"
                            value={tt.quantity}
                            onChange={(e) => updateTicketType(index, 'quantity', parseInt(e.target.value) || 1)}
                          />
                        </div>
                        <div className="flex items-end gap-4">
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              checked={tt.early_bird}
                              onCheckedChange={(c) => updateTicketType(index, 'early_bird', c)}
                            />
                            <Label className="cursor-pointer text-sm">Early Bird</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox 
                              checked={tt.vip}
                              onCheckedChange={(c) => updateTicketType(index, 'vip', c)}
                            />
                            <Label className="cursor-pointer text-sm">VIP</Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-event-btn">
                  {saving ? "Saving..." : selectedEvent ? "Update Event" : "Create Event"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Event</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{selectedEvent?.title}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
